import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Svg, Circle, Path, Link } from '@react-pdf/renderer';
import heroBg from '../assets/hero-bg.png';
import { cleanAddress, getAddressParts } from './addressUtils';
import { detectJurisdiction, getJurisdictionInfo } from '../components/Modal/MapModal/utils/jurisdictionHelpers';

// --- Professional Theme Configuration ---
const colors = {
    indigo: '#4f46e5',
    indigoDark: '#020617',
    indigoLight: '#818cf8',
    slate: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        800: '#1e293b',
        900: '#0f172a',
    },
    emerald: '#10b981',
    amber: '#f59e0b',
    white: '#ffffff',
};

const styles = StyleSheet.create({
    page: {
        padding: 0,
        backgroundColor: colors.indigoDark,
        fontFamily: 'Helvetica',
    },
    // --- Page 1: Creative Executive Cover ---
    coverContainer: {
        height: '100%',
        position: 'relative',
        backgroundColor: colors.indigoDark,
        padding: 0,
    },
    coverImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.35,
        objectFit: 'cover',
    },
    accentBar: {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 8,
        backgroundColor: colors.indigo,
        zIndex: 30,
    },
    coverContent: {
        height: '100%',
        padding: 80,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        zIndex: 20,
    },
    topBranding: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    middleSection: {
        marginTop: 40,
    },
    auditBadge: {
        backgroundColor: colors.indigo,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 4,
        alignSelf: 'flex-start',
        marginBottom: 20,
    },
    auditBadgeText: {
        fontSize: 10,
        fontWeight: 'black',
        color: colors.white,
        textTransform: 'uppercase',
        letterSpacing: 3,
    },
    hugeTitle: {
        fontSize: 72,
        fontWeight: 'black',
        color: colors.white,
        letterSpacing: -4,
        lineHeight: 0.9,
    },
    subHeadline: {
        fontSize: 18,
        fontWeight: 'bold',
        color: colors.indigoLight,
        marginTop: 20,
        letterSpacing: 2,
        textTransform: 'uppercase',
    },
    bottomMetadata: {
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.1)',
        paddingTop: 40,
    },
    propertyLabel: {
        fontSize: 10,
        fontWeight: 'black',
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: 4,
        marginBottom: 10,
    },
    addressLine: {
        fontSize: 22,
        fontWeight: 'black',
        color: colors.white,
        marginBottom: 10,
        letterSpacing: -0.5,
    },
    dateLine: {
        fontSize: 11,
        color: colors.indigoLight,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 3,
    },
    // --- Standard Content Pages ---
    contentPage: {
        padding: 50,
        backgroundColor: colors.white,
        fontFamily: 'Helvetica',
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 30,
        borderBottomWidth: 1,
        borderBottomColor: colors.slate[100],
        paddingBottom: 20,
    },
    sectionHeading: {
        fontSize: 10,
        fontWeight: 'black',
        color: colors.indigo,
        textTransform: 'uppercase',
        letterSpacing: 2,
        marginBottom: 15,
    },
    // --- Shared Components ---
    statLine: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(0,0,0,0.03)',
    },
    statLabel: {
        fontSize: 9,
        color: colors.slate[500],
        fontWeight: 'bold',
    },
    statValue: {
        fontSize: 10,
        fontWeight: 'black',
        color: colors.slate[900],
    },
    noteBox: {
        backgroundColor: colors.slate[50],
        padding: 18,
        borderRadius: 16,
        borderLeftWidth: 5,
        borderLeftColor: colors.amber,
    },
    noteText: {
        fontSize: 9,
        color: colors.slate[700],
        lineHeight: 1.6,
    },
    // --- Page 2: Summary Card ---
    zoningSummaryCard: {
        backgroundColor: colors.slate[900],
        borderRadius: 16,
        padding: 25,
        marginBottom: 35,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    zoningMetric: {
        flex: 1,
    },
    zoningLabel: {
        fontSize: 8,
        fontWeight: 'bold',
        color: colors.indigoLight,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 6,
    },
    zoningValue: {
        fontSize: 22,
        fontWeight: 'black',
        color: colors.white,
    },
    // --- Analysis Cards ---
    analysisCard: {
        flex: 1,
        backgroundColor: colors.slate[50],
        borderRadius: 16,
        padding: 20,
        borderWidth: 1,
        borderColor: colors.slate[100],
    },
    // --- ADU Page Specific ---
    aduHero: {
        backgroundColor: colors.slate[900],
        borderRadius: 20,
        padding: 30,
        marginBottom: 30,
        color: colors.white,
    },
    aduPotential: {
        fontSize: 28,
        fontWeight: 'black',
        marginBottom: 10,
    },
    aduDesc: {
        fontSize: 10,
        color: colors.slate[400],
        lineHeight: 1.5,
    },
    badgeRow: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 15,
    },
    badge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        fontSize: 8,
        fontWeight: 'bold',
    },
    // --- Footer ---
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 50,
        right: 50,
        borderTopWidth: 1,
        borderTopColor: colors.slate[100],
        paddingTop: 15,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerText: {
        fontSize: 8,
        fontWeight: 'bold',
        color: colors.slate[300],
        textTransform: 'uppercase',
    },
    // --- Table Styles ---
    table: {
        display: 'table',
        width: 'auto',
        borderStyle: 'solid',
        borderColor: colors.slate[100],
        borderWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: colors.slate[900],
        borderRadius: 8,
        marginBottom: 8,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: colors.slate[50],
        paddingVertical: 8,
        alignItems: 'center',
    },
    tableColHeader: {
        flex: 1,
        padding: 8,
    },
    tableCol: {
        flex: 1,
        padding: 8,
    },
    tableCellHeader: {
        fontSize: 8,
        fontWeight: 'black',
        color: colors.white,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    tableCell: {
        fontSize: 9,
        fontWeight: 'bold',
        color: colors.slate[800],
    },
    // --- Compliance & Timeline ---
    complianceCard: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 15,
        backgroundColor: colors.slate[50],
        borderRadius: 12,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: colors.slate[100],
    },
    timelineStep: {
        flexDirection: 'row',
        marginBottom: 20,
    },
    stepDot: {
        width: 12,
        height: 12,
        borderRadius: 6,
        backgroundColor: colors.indigo,
        marginTop: 4,
        marginRight: 15,
        borderWidth: 3,
        borderColor: 'rgba(79, 70, 229, 0.2)',
    },
    stepContent: {
        flex: 1,
    }
});

// --- UI Helper Components ---
const BrandingLogo = ({ scale = 1, inverse = false }) => (
    <Link src="https://developmentanalyzer.com" style={{ textDecoration: 'none' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Svg width={36 * scale} height={36 * scale} viewBox="0 0 40 40">
                <Circle cx="20" cy="11" r="0.75" fill={inverse ? colors.indigoLight : "#2563EB"} fillOpacity="0.8" />
                <Circle cx="15" cy="14" r="0.75" fill={inverse ? colors.indigoLight : "#2563EB"} fillOpacity="0.8" />
                <Circle cx="25" cy="14" r="0.75" fill={inverse ? colors.indigoLight : "#2563EB"} fillOpacity="0.8" />
                <Path d="M5 15V26L20 35V24L5 15Z" fill={inverse ? colors.white : colors.slate[900]} />
                <Path d="M20 24L35 15V26L20 35V24Z" fill={inverse ? colors.indigoLight : "#2563EB"} />
                <Path d="M20 6L35 15L20 24L5 15L20 6Z" fill={inverse ? colors.white : colors.slate[900]} fillOpacity={0.15} />
            </Svg>
            <View style={{ marginLeft: 15 }}>
                <Text style={{ fontSize: 20 * scale, fontWeight: 'black', color: inverse ? colors.white : colors.slate[900], letterSpacing: -0.5 }}>
                    Development<Text style={{ color: inverse ? colors.indigoLight : colors.indigo }}>Analyzer</Text>
                </Text>
                <Text style={{ fontSize: 8 * scale, fontWeight: 'bold', color: inverse ? 'rgba(255,255,255,0.6)' : colors.slate[400], textTransform: 'uppercase', letterSpacing: 3, marginTop: 3 }}>
                    Systems Intelligence
                </Text>
            </View>
        </View>
    </Link>
);

const AnalysisRow = ({ label, value, unit, highlight = false, color = null, inverse = false }) => (
    <View style={styles.statLine}>
        <Text style={[styles.statLabel, inverse && { color: 'rgba(255,255,255,0.5)' }]}>{label}</Text>
        <Text style={[styles.statValue, highlight && { color: color || colors.indigo }, inverse && { color: colors.white }]}>
            {value}{unit && <Text style={{ fontSize: 8, color: inverse ? 'rgba(255,255,255,0.3)' : colors.slate[300] }}> {unit}</Text>}
        </Text>
    </View>
);

const TopDisclaimer = ({ inverse = false }) => (
    <View style={{ 
        marginBottom: 15, 
        backgroundColor: inverse ? 'rgba(255,255,255,0.05)' : colors.slate[50],
        padding: 6,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: inverse ? 'rgba(255,255,255,0.1)' : colors.slate[100],
    }}>
        <Text style={{ 
            fontSize: 7, 
            color: inverse ? 'rgba(255,255,255,0.5)' : colors.slate[500], 
            textAlign: 'center', 
            fontWeight: 'bold',
            letterSpacing: 0.2
        }}>
            TRUSTED GIS DATA: <Text style={{ fontWeight: 'normal' }}>Powered by official ArcGIS & Municipal records. This preliminary audit requires professional verification prior to any financial commitment.</Text>
        </Text>
    </View>
);

const Footer = ({ pageNumber, totalPages, city }) => (
    <View style={styles.footer}>
        <View>
            <Text style={styles.footerText}>Official Feasibility Audit Report</Text>
            <Text style={[styles.footerText, { marginTop: 4, textTransform: 'none', color: colors.slate[400], fontWeight: 'normal' }]}>
                <Link src="mailto:support@privateopenhouse.com" style={{ color: colors.slate[400], textDecoration: 'none' }}>support@privateopenhouse.com</Link>  •  (650) 388-0730  •  {city}
            </Text>
        </View>
        <Text style={styles.footerText}>Page {pageNumber} of {totalPages}</Text>
    </View>
);

// --- Report ---
const AnalysisReportPDF = ({ analysis, evidence, formattedDate }) => {
    const {
        address, apn, city_code, jurisdictionName, zone_code, parcel_zone_code,
        avg_slope_percent_LAH, slope_percent, slopeCat,
        parcelFt2, parcel_area_ft2, acres, parcel_area_acres,
        mfa_ft2, mda_ft2, gross_building_area_ft2, living_area_ft2,
        convMetric, notes,
        contour_count, contour_total_length_ft,
        demContourTrace, demContourError,
        setbacks, heightFt, lotCovPct, permitted,
        aduEligible, jaduEligible, duplexEleg, designRev,
        timeline, existingMfaArea,
        requires_cdp, gis_engine, coordinate_system,
        mapAnalysisStats, calcResult
    } = analysis;

    const aerialSnapshot = evidence?.aerialSnapshot?.dataUrl;
    const aerialSnapshotNoContours = evidence?.aerialSnapshotNoContours?.dataUrl;

    const slopeVal = parseFloat(avg_slope_percent_LAH ?? slope_percent ?? 0).toFixed(1);
    const pFt2 = parseFloat(parcelFt2 ?? parcel_area_ft2 ?? 0);
    const pAcres = parseFloat(acres ?? parcel_area_acres ?? (pFt2 / 43560)).toFixed(3);
    const lufValue = convMetric != null ? convMetric.toFixed(2) : pAcres;
    const existingFootprint = parseFloat(gross_building_area_ft2 || 0);
    const livingArea = parseFloat(living_area_ft2 || 0);
    const municipalNotes = Array.isArray(notes) ? notes : notes?.municipal_notes || [];
    
    // Dynamic Labels for Municipal Terminology
    const jur = detectJurisdiction(address);
    const jurInfo = getJurisdictionInfo(jur);
    const displayMdaLabel = jurInfo?.mdaLabel || analysis.mda_label || "Max Development (MDA)";
    const displayMfaLabel = jurInfo?.mfaLabel || analysis.mfa_label || "Max Floor Area (MFA)";
    
    // Extract city from address as fallback
    const getCityFromAddress = (addr) => {
        if (!addr) return '';
        const parts = addr.split(',');
        if (parts.length >= 3) {
            return parts[parts.length - 3].trim();
        }
        return '';
    };
    const displayCity = jurisdictionName || city_code || getCityFromAddress(address);

    // Map Statistics (LiDAR)
    const s = mapAnalysisStats || {};
    const totalCells = (s.flat || 0) + (s.moderate || 0) + (s.steep || 0);
    const flatPct = totalCells > 0 ? (s.flat / totalCells) * 100 : 0;
    const modPct = totalCells > 0 ? (s.moderate / totalCells) * 100 : 0;
    const steepPct = totalCells > 0 ? (s.steep / totalCells) * 100 : 0;
    
    const mfaArea = parseFloat(mfa_ft2 || 0);
    const mdaArea = parseFloat(mda_ft2 || 0);
    const totalTerrainArea = Math.round(mfaArea + mdaArea);
    const buildableYield = s.totalArea > 0 
        ? Math.round(((s.flatArea || 0) + (s.moderateArea || 0)) / s.totalArea * 100)
        : Math.round(100 - slopeVal);

    // Contour Data
    const contourDetails = demContourTrace?.contour_details || [];
    const hasContours = contourDetails.length > 0;

    const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size));
        }
        return chunks;
    };

    const contourPages = hasContours ? chunkArray(contourDetails, 20) : [[]];
    const extraPagesCount = Math.max(0, contourPages.length - 1);
    const totalPages = 12 + extraPagesCount;

    // ADU Calcs
    const currentMfa = existingMfaArea ?? livingArea;
    const attachedMax = Math.min(1200, Math.max(800, Math.round(currentMfa * 0.5)));
    const detachedMax = 1200;
    const jaduMax = 500;

    return (
        <Document title={`Feasibility Audit - ${cleanAddress(address)}`}>
            {/* PAGE 1: COVER */}
            <Page size="A4" style={styles.page}>
                <View style={styles.coverContainer}>
                    <Image src={heroBg} style={styles.coverImage} />
                    <View style={styles.accentBar} />
                    <View style={styles.coverContent}>
                        <TopDisclaimer inverse />
                        <View style={styles.topBranding}>
                            <BrandingLogo scale={1.2} inverse />
                            <View style={{ alignItems: 'right' }}>
                                <Text style={{ fontSize: 10, fontWeight: 'black', color: 'rgba(255,255,255,0.4)', letterSpacing: 2 }}>REPORT ID</Text>
                                <Text style={{ fontSize: 12, fontWeight: 'black', color: colors.white }}>#{analysis.id?.toString()?.slice(-8).toUpperCase() || "DA-2026-X"}</Text>
                            </View>
                        </View>
                        <View style={styles.middleSection}>
                            <View style={styles.auditBadge}>
                                <Text style={styles.auditBadgeText}>Professional Audit</Text>
                            </View>
                            <Text style={styles.hugeTitle}>FEASIBILITY</Text>
                            <Text style={[styles.hugeTitle, { color: colors.indigoLight }]}>ANALYSIS</Text>
                            <Text style={styles.subHeadline}>BEFORE INVESTING IN REAL ESTATE</Text>
                        </View>
                        <View style={styles.bottomMetadata}>
                            <Text style={styles.propertyLabel}>Target Property</Text>
                            {(() => {
                                const parts = getAddressParts(address);
                                return parts.map((part, i) => (
                                    <Text key={i} style={i === 0 ? styles.addressLine : { fontSize: 13, fontWeight: 'bold', color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
                                        {i > 0 ? '+ ' : ''}{part}
                                    </Text>
                                ));
                            })()}
                            <Text style={styles.dateLine}>ISSUED: {formattedDate}</Text>
                        </View>
                    </View>
                </View>
            </Page>

            {/* PAGE 2: SITE CONTEXT MAP */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Site Context Map</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            High-Resolution GIS Aerial Analysis
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                {aerialSnapshot ? (
                    <View style={{ 
                        width: '100%', 
                        height: 450, 
                        backgroundColor: colors.slate[100], 
                        borderRadius: 24, 
                        overflow: 'hidden',
                        borderWidth: 1,
                        borderColor: colors.slate[200],
                        marginBottom: 30
                    }}>
                        <Image src={aerialSnapshot} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </View>
                ) : (
                    <View style={{ 
                        width: '100%', 
                        height: 450, 
                        backgroundColor: colors.slate[50], 
                        borderRadius: 24, 
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderWidth: 2,
                        borderColor: colors.slate[100],
                        borderStyle: 'dashed',
                        marginBottom: 30
                    }}>
                        <Text style={{ fontSize: 14, fontWeight: 'black', color: colors.slate[300] }}>MAP DATA AWAITING GENERATION</Text>
                        <Text style={{ fontSize: 8, color: colors.slate[300], marginTop: 10, textAlign: 'center', maxWidth: 200 }}>
                            To include a live GIS snapshot, please open the Map Engine for this property before exporting.
                        </Text>
                    </View>
                )}

                <View style={styles.noteBox}>
                    <Text style={[styles.noteText, { fontWeight: 'bold', marginBottom: 5 }]}>MAP LEGEND & CONTEXT</Text>
                    <Text style={styles.noteText}>
                        The above imagery represents the property boundaries and topographical conditions as captured by the GIS engine. This snapshot includes the currently active layers (Contours, Terrain Grid, and Test-Fit Structures) at the time of export.
                    </Text>
                </View>

                <Footer pageNumber={2} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 3: BASE SITE ANALYSIS (NO CONTOURS) */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Base Site Analysis</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Aerial View Without Topographical Overlays
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                {aerialSnapshotNoContours ? (
                    <View style={{ 
                        width: '100%', 
                        height: 450, 
                        backgroundColor: colors.slate[100], 
                        borderRadius: 24, 
                        overflow: 'hidden',
                        borderWidth: 1,
                        borderColor: colors.slate[200],
                        marginBottom: 30
                    }}>
                        <Image src={aerialSnapshotNoContours} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </View>
                ) : (
                    <View style={{ 
                        width: '100%', 
                        height: 450, 
                        backgroundColor: colors.slate[50], 
                        borderRadius: 24, 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        borderWidth: 2, 
                        borderColor: colors.slate[100], 
                        borderStyle: 'dashed',
                        marginBottom: 30
                    }}>
                        <Text style={{ fontSize: 14, fontWeight: 'black', color: colors.slate[300] }}>BASE MAP DATA AWAITING GENERATION</Text>
                        <Text style={{ fontSize: 8, color: colors.slate[300], marginTop: 10, textAlign: 'center', maxWidth: 200 }}>
                            Please open the Map Engine for this property to generate all required map variants.
                        </Text>
                    </View>
                )}

                <View style={styles.noteBox}>
                    <Text style={[styles.noteText, { fontWeight: 'bold', marginBottom: 5 }]}>SITE BASE CONTEXT</Text>
                    <Text style={styles.noteText}>
                        This view provides a clear, unobstructed aerial perspective of the existing site conditions and surrounding infrastructure. It is used to verify property boundaries and existing structures without the visual interference of topographical contour data.
                    </Text>
                </View>

                <Footer pageNumber={3} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 3: EXECUTIVE SUMMARY */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        {(() => {
                            const parts = getAddressParts(address);
                            return (
                                <View>
                                    <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: parts.length > 1 ? 3 : 5 }}>{parts[0]}</Text>
                                    {parts.slice(1).map((part, i) => (
                                        <Text key={i} style={{ fontSize: 11, fontWeight: 'bold', color: colors.indigo, marginBottom: 2 }}>+ {part}</Text>
                                    ))}
                                </View>
                            );
                        })()}
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            APN: {apn} • {jurisdictionName || city_code}
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <Text style={styles.sectionHeading}>Zoning Engine Constraints</Text>
                <View style={styles.zoningSummaryCard}>
                    {jur === 'los_gatos' ? (
                        <>
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{calcResult?.formulaLabel || "Table 2 Floor Area"}</Text>
                                <Text style={styles.zoningValue}>
                                    {calcResult?.farValue ? `${(calcResult.farValue * 100).toFixed(1)}%` : "N/A"}
                                    {convMetric != null && (
                                        <Text style={{ fontSize: 10, color: colors.slate[400] }}>
                                            {` (${convMetric.toFixed(2)} ac)`}
                                        </Text>
                                    )}
                                </Text>
                            </View>
                            <View style={{ width: 2, backgroundColor: 'rgba(255,255,255,0.1)', height: 40, marginHorizontal: 15 }} />
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>Net Site Area (NSA)</Text>
                                <Text style={styles.zoningValue}>
                                    {calcResult?.netSiteArea ? Math.round(calcResult.netSiteArea).toLocaleString() : "N/A"}
                                    <Text style={{ fontSize: 12, color: colors.indigoLight }}> ft²</Text>
                                </Text>
                            </View>
                            <View style={{ width: 2, backgroundColor: 'rgba(255,255,255,0.1)', height: 40, marginHorizontal: 15 }} />
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{displayMfaLabel}</Text>
                                <Text style={styles.zoningValue}>
                                    {parseFloat(mfa_ft2 || 0).toLocaleString()}
                                    <Text style={{ fontSize: 12, color: colors.indigoLight }}> ft²</Text>
                                </Text>
                            </View>
                        </>
                    ) : jur === 'saratoga' ? (
                        <>
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>Net Site Area (NSA)</Text>
                                <Text style={styles.zoningValue}>
                                    {calcResult?.netSiteArea ? Math.round(calcResult.netSiteArea).toLocaleString() : "N/A"}
                                    <Text style={{ fontSize: 12, color: colors.indigoLight }}> ft²</Text>
                                    {calcResult?.netSiteArea && (
                                        <Text style={{ fontSize: 10, color: colors.slate[400] }}>
                                            {` (${(calcResult.netSiteArea / 43560).toFixed(3)} ac)`}
                                        </Text>
                                    )}
                                </Text>
                            </View>
                            <View style={{ width: 2, backgroundColor: 'rgba(255,255,255,0.1)', height: 40, marginHorizontal: 15 }} />
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{displayMdaLabel}</Text>
                                <Text style={styles.zoningValue}>
                                    {parseFloat(mda_ft2 || 0).toLocaleString()}
                                    <Text style={{ fontSize: 12, color: colors.indigoLight }}> ft²</Text>
                                </Text>
                            </View>
                            <View style={{ width: 2, backgroundColor: 'rgba(255,255,255,0.1)', height: 40, marginHorizontal: 15 }} />
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{displayMfaLabel}</Text>
                                <Text style={styles.zoningValue}>
                                    {parseFloat(mfa_ft2 || 0).toLocaleString()}
                                    <Text style={{ fontSize: 12, color: colors.indigoLight }}> ft²</Text>
                                </Text>
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{displayMdaLabel}</Text>
                                <Text style={styles.zoningValue}>{parseFloat(mda_ft2 || 0).toLocaleString()} <Text style={{ fontSize: 12, color: colors.indigoLight }}>ft²</Text></Text>
                            </View>
                            <View style={{ width: 2, backgroundColor: 'rgba(255,255,255,0.1)', height: 40, marginHorizontal: 30 }} />
                            <View style={styles.zoningMetric}>
                                <Text style={styles.zoningLabel}>{displayMfaLabel}</Text>
                                <Text style={styles.zoningValue}>{parseFloat(mfa_ft2 || 0).toLocaleString()} <Text style={{ fontSize: 12, color: colors.indigoLight }}>ft²</Text></Text>
                            </View>
                        </>
                    )}
                </View>

                <View style={{ flexDirection: 'row', gap: 20, marginBottom: 20 }}>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Terrain Analysis</Text>
                        <AnalysisRow label="Avg Parcel Slope" value={slopeVal} unit="%" highlight />
                        <AnalysisRow label="Terrain Type" value={slopeCat || "Gentle / Flat"} />
                        <AnalysisRow label="LiDAR Samples" value={contour_count || 0} />
                        <AnalysisRow label="Total Length" value={Math.round(contour_total_length_ft || 0).toLocaleString()} unit="ft" />
                    </View>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Regional Boundaries</Text>
                        <AnalysisRow label="Existing Footprint" value={existingFootprint.toLocaleString()} unit="ft²" highlight />
                        <AnalysisRow label="Lot Size" value={pFt2.toLocaleString()} unit="ft²" />
                        <AnalysisRow label="Parcel Area" value={pAcres} unit="ac" />
                        {calcResult?.netSiteArea && (
                            <AnalysisRow label="Net Site Area" value={Math.round(calcResult.netSiteArea).toLocaleString()} unit="ft²" />
                        )}
                    </View>
                </View>

                <View style={{ marginTop: 25 }}>
                    <Text style={styles.sectionHeading}>Municipal & Planning Notes</Text>
                    <View style={styles.noteBox}>
                        {municipalNotes.length > 0 ? (
                            municipalNotes.slice(0, 5).map((note, i) => (
                                <View key={i} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
                                    <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: colors.amber, marginTop: 6, marginRight: 12 }} />
                                    <Text style={styles.noteText}>{note}</Text>
                                </View>
                            ))
                        ) : (
                            <Text style={[styles.noteText, { fontStyle: 'italic', textAlign: 'center' }]}>No specific municipal overrides detected.</Text>
                        )}
                    </View>
                </View>

                <Footer pageNumber={4} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 4: TOPOGRAPHIC INVENTORY */}
            {contourPages.map((pageContours, pageIdx) => {
                const currentPageNum = 5 + pageIdx;
                const isFirstPage = pageIdx === 0;

                return (
                    <Page key={pageIdx} size="A4" style={styles.contentPage}>
                        <TopDisclaimer />
                        <View style={styles.headerRow}>
                            <View style={{ flex: 1, marginRight: 20 }}>
                                <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>
                                    Topographic Inventory{pageIdx > 0 ? ' (Cont.)' : ''}
                                </Text>
                                <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                                    Survey-Standard LiDAR Record
                                </Text>
                            </View>
                            <BrandingLogo scale={0.75} />
                        </View>

                        {hasContours ? (
                            <View>
                                {isFirstPage && (
                                    <View style={[styles.zoningSummaryCard, { marginBottom: 25 }]}>
                                        <View style={styles.zoningMetric}>
                                            <Text style={styles.zoningLabel}>Grid Interval</Text>
                                            <Text style={styles.zoningValue}>{demContourTrace?.interval_ft || 2} <Text style={{ fontSize: 12, color: colors.indigoLight }}>FT</Text></Text>
                                        </View>
                                        <View style={styles.zoningMetric}>
                                            <Text style={styles.zoningLabel}>Detected Lines</Text>
                                            <Text style={styles.zoningValue}>{demContourTrace?.contour_count || 0}</Text>
                                        </View>
                                        <View style={styles.zoningMetric}>
                                            <Text style={styles.zoningLabel}>Total Trace (L)</Text>
                                            <Text style={styles.zoningValue}>{Math.round(demContourTrace?.contour_total_length_ft || 0).toLocaleString()} <Text style={{ fontSize: 12, color: colors.indigoLight }}>FT</Text></Text>
                                        </View>
                                    </View>
                                )}

                                <View style={{ flexDirection: 'row', gap: 15 }}>
                                    <View style={{ flex: 1 }}>
                                        <View style={styles.table}>
                                            <View style={styles.tableHeader}>
                                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Elevation</Text></View>
                                                <View style={[styles.tableColHeader, { textAlign: 'right' }]}><Text style={styles.tableCellHeader}>Length</Text></View>
                                                <View style={[styles.tableColHeader, { textAlign: 'right' }]}><Text style={styles.tableCellHeader}>%</Text></View>
                                            </View>
                                            {pageContours.slice(0, 10).map((c, i) => (
                                                <View key={i} style={[styles.tableRow, i % 2 === 0 && { backgroundColor: colors.slate[50] }]}>
                                                    <View style={styles.tableCol}><Text style={[styles.tableCell, { fontWeight: 'black' }]}>{c.elevation} FT</Text></View>
                                                    <View style={[styles.tableCol, { textAlign: 'right' }]}><Text style={styles.tableCell}>{Math.round(c.length).toLocaleString()}</Text></View>
                                                    <View style={[styles.tableCol, { textAlign: 'right' }]}><Text style={[styles.tableCell, { color: colors.indigo, fontSize: 8 }]}>{((c.length / (demContourTrace?.contour_total_length_ft || 1)) * 100).toFixed(1)}%</Text></View>
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                    <View style={{ flex: 1 }}>
                                        <View style={styles.table}>
                                            <View style={styles.tableHeader}>
                                                <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Elevation</Text></View>
                                                <View style={[styles.tableColHeader, { textAlign: 'right' }]}><Text style={styles.tableCellHeader}>Length</Text></View>
                                                <View style={[styles.tableColHeader, { textAlign: 'right' }]}><Text style={styles.tableCellHeader}>%</Text></View>
                                            </View>
                                            {pageContours.slice(10, 20).map((c, i) => (
                                                <View key={i} style={[styles.tableRow, i % 2 === 0 && { backgroundColor: colors.slate[50] }]}>
                                                    <View style={styles.tableCol}><Text style={[styles.tableCell, { fontWeight: 'black' }]}>{c.elevation} FT</Text></View>
                                                    <View style={[styles.tableCol, { textAlign: 'right' }]}><Text style={styles.tableCell}>{Math.round(c.length).toLocaleString()}</Text></View>
                                                    <View style={[styles.tableCol, { textAlign: 'right' }]}><Text style={[styles.tableCell, { color: colors.indigo, fontSize: 8 }]}>{((c.length / (demContourTrace?.contour_total_length_ft || 1)) * 100).toFixed(1)}%</Text></View>
                                                </View>
                                            ))}
                                        </View>
                                    </View>
                                </View>

                            </View>
                        ) : (
                            <View style={{ marginTop: 100, alignItems: 'center' }}>
                                <Text style={{ fontSize: 24, fontWeight: 'black', color: colors.slate[900], marginBottom: 10 }}>Contour Intelligence</Text>
                                <Text style={{ fontSize: 12, color: colors.slate[400], textAlign: 'center', maxWidth: 300 }}>
                                    {demContourError || "Launch the Map Engine to generate high-fidelity topographic contour lines."}
                                </Text>
                            </View>
                        )}

                        <Footer pageNumber={currentPageNum} totalPages={totalPages} city={displayCity} />
                    </Page>
                );
            })}

            {/* PAGE 5: ZONING & BUILDABILITY */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Zoning Standards</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            {(() => {
                                const rawZone = zone_code || parcel_zone_code || "R-1";
                                const clean = rawZone.trim();
                                if (clean === "R1E") return "R1";
                                if (clean === "R-1E" || clean === "R-1:E" || clean === "R-1-E" || clean === "R1-E") return "R-1";
                                return clean;
                            })()} District Code Audit
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <View style={{ flexDirection: 'row', gap: 20, marginBottom: 30 }}>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Dimensional Standards</Text>
                        <AnalysisRow label="Front Setback" value={setbacks?.front_ft || "N/A"} unit="ft" highlight />
                        <AnalysisRow label="Rear Setback" value={setbacks?.rear_ft || "N/A"} unit="ft" />
                        <AnalysisRow label="Side Setback" value={setbacks?.side_ft || "N/A"} unit="ft" />
                        <AnalysisRow label="Max Height" value={heightFt || "N/A"} unit="ft" highlight color={colors.emerald} />
                        <AnalysisRow label="Lot Coverage" value={lotCovPct != null ? `${lotCovPct}%` : "Verify"} />
                    </View>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Permitted Uses</Text>
                        <View style={styles.badgeRow}>
                            {Array.isArray(permitted) && permitted.length > 0 ? (
                                permitted.map((use, i) => (
                                    <View key={i} style={[styles.badge, { backgroundColor: colors.slate[900], color: colors.white }]}>
                                        <Text>{use}</Text>
                                    </View>
                                ))
                            ) : (
                                <Text style={{ fontSize: 9, color: colors.slate[400], fontStyle: 'italic' }}>Standard Residential Uses</Text>
                            )}
                        </View>
                        <View style={{ marginTop: 25 }}>
                            <AnalysisRow label="ADU Eligible" value={aduEligible ? "✓ YES" : "✗ NO"} color={aduEligible ? colors.emerald : colors.amber} highlight />
                            <AnalysisRow label="JADU Eligible" value={jaduEligible ? "✓ YES" : "✗ NO"} color={jaduEligible ? colors.emerald : colors.amber} />
                            <AnalysisRow label="Duplex (SB9)" value={duplexEleg ? "✓ YES" : "✗ NO"} color={duplexEleg ? colors.emerald : colors.amber} />
                        </View>
                    </View>
                </View>

                <View style={{ marginTop: 20 }}>
                    <Text style={styles.sectionHeading}>Documentation Prerequisites</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 15 }}>
                        {[
                            { doc: "Topographic Survey", req: true },
                            { doc: "Geotechnical Report", req: slopeVal > 15 },
                            { doc: "Coastal Permit (CDP)", req: requires_cdp },
                            { doc: "Design Review", req: designRev },
                        ].map((item, i) => (
                            <View key={i} style={{ width: '47%', padding: 12, backgroundColor: colors.slate[50], borderRadius: 12, borderLeftWidth: 3, borderLeftColor: item.req ? colors.amber : colors.emerald }}>
                                <Text style={{ fontSize: 9, fontWeight: 'black', color: colors.slate[800] }}>{item.doc}</Text>
                                <Text style={{ fontSize: 7, color: colors.slate[400], marginTop: 2 }}>{item.req ? "REQUIRED" : "PASSED / NOT REQUIRED"}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <Footer pageNumber={6 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 6: TERRAIN INTELLIGENCE */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Terrain Intelligence</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Slope Distribution & Buildability Complexity
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <View style={{ backgroundColor: colors.slate[900], borderRadius: 24, padding: 25, marginBottom: 25 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.indigo, borderSize: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                                <Text style={{ fontSize: 9, color: colors.indigoLight, fontWeight: 'black', letterSpacing: 2 }}>COMPLEXITY SCORE</Text>
                            </View>
                            <Text style={{ fontSize: 28, fontWeight: 'black', color: colors.white }}>{slopeVal > 15 ? "High Complexity" : "Standard Build"}</Text>
                            <Text style={{ fontSize: 8, color: colors.slate[400], marginTop: 5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>Topographical Analysis & Buildability</Text>
                        </View>
                        <View style={{ width: 75, height: 75, borderRadius: 37.5, borderSize: 5, borderColor: buildableYield < 80 ? colors.amber : colors.emerald, alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.white }}>{buildableYield}%</Text>
                            <Text style={{ fontSize: 6, color: 'rgba(255,255,255,0.4)', fontWeight: 'black' }}>FEASIBLE</Text>
                        </View>
                    </View>

                    {/* LiDAR Profile Analysis - Mini Viz */}
                    <View style={{ marginTop: 20, paddingTop: 20, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)' }}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.emerald }} />
                                <Text style={{ fontSize: 8, color: colors.slate[300], fontWeight: 'bold' }}>LiDAR Profile Analysis</Text>
                            </View>
                            <Text style={{ fontSize: 8, color: colors.indigoLight, fontWeight: 'black' }}>HIGH-FIDELITY</Text>
                        </View>
                        
                        <View style={{ height: 4, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 2, flexDirection: 'row', overflow: 'hidden' }}>
                            <View style={{ width: `${flatPct}%`, backgroundColor: colors.emerald }} />
                            <View style={{ width: `${modPct}%`, backgroundColor: colors.amber }} />
                            <View style={{ width: `${steepPct}%`, backgroundColor: '#f43f5e' }} />
                        </View>

                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
                            <View>
                                <Text style={{ fontSize: 10, fontWeight: 'black', color: colors.emerald }}>{Math.round(flatPct)}%</Text>
                                <Text style={{ fontSize: 6, color: colors.slate[500], fontWeight: 'bold' }}>FLAT</Text>
                            </View>
                            <View style={{ alignItems: 'center' }}>
                                <Text style={{ fontSize: 10, fontWeight: 'black', color: colors.amber }}>{Math.round(modPct)}%</Text>
                                <Text style={{ fontSize: 6, color: colors.slate[500], fontWeight: 'bold' }}>MODERATE</Text>
                            </View>
                            <View style={{ alignItems: 'right' }}>
                                <Text style={{ fontSize: 10, fontWeight: 'black', color: '#f43f5e' }}>{Math.round(steepPct)}%</Text>
                                <Text style={{ fontSize: 6, color: colors.slate[500], fontWeight: 'bold' }}>STEEP</Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={{ flexDirection: 'row', gap: 20, marginBottom: 25 }}>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Slope Breakdown</Text>
                        <AnalysisRow label="Average Gradient" value={slopeVal} unit="%" highlight />
                        <AnalysisRow label="Terrain Classification" value={slopeCat || "Gentle"} />
                        <AnalysisRow label="Total Terrain Area" value={totalTerrainArea.toLocaleString()} unit="ft²" highlight color={colors.emerald} />
                        <View style={{ marginTop: 2, marginLeft: 0 }}>
                            <Text style={{ fontSize: 7, color: colors.slate[400], fontWeight: 'bold' }}>
                                ({analysis.mfa_label || "MFA"}: {Math.round(mfaArea).toLocaleString()} + {analysis.mda_label || "MDA"}: {Math.round(mdaArea).toLocaleString()})
                            </Text>
                        </View>
                    </View>
                    <View style={styles.analysisCard}>
                        <Text style={styles.sectionHeading}>Elevation Profiles</Text>
                        <AnalysisRow label="Total Contour Samples" value={contour_count || 0} highlight color={colors.indigo} />
                        <AnalysisRow label="Cumulative Trace" value={Math.round(contour_total_length_ft || 0).toLocaleString()} unit="ft" />
                        <AnalysisRow label="Accuracy Engine" value={gis_engine || "LIDAR-V2"} />
                        <AnalysisRow 
                            label={
                                jur === 'los_gatos' 
                                    ? (calcResult?.formulaLabel || "Table 2 Floor Area") 
                                    : jur === 'saratoga' 
                                        ? "Net Site Area (NSA)" 
                                        : "Development Limit"
                            } 
                            value={
                                jur === 'los_gatos' && calcResult?.farValue 
                                    ? `${(calcResult.farValue * 100).toFixed(1)}% (${parseFloat(lufValue).toFixed(2)} ac)` 
                                    : jur === 'saratoga' && calcResult?.netSiteArea 
                                        ? `${Math.round(calcResult.netSiteArea).toLocaleString()} ft² (${(calcResult.netSiteArea / 43560).toFixed(3)} ac)` 
                                        : lufValue
                            } 
                            unit={jur === 'los_gatos' || jur === 'saratoga' ? "" : "ac"} 
                        />
                    </View>
                </View>

                <Text style={styles.sectionHeading}>Site Preparation Requirements</Text>
                <View style={{ gap: 10 }}>
                    {[
                        { label: "Grading Complexity", val: slopeVal > 15 ? "Significant" : "Minimal", sub: "Based on natural grade average." },
                        { label: "Foundation Standard", val: slopeVal > 20 ? "Deep / Hillside" : "Standard Slab", sub: "Requires geotechnical verification." },
                        { label: "Stormwater Management", val: slopeVal > 10 ? "Required" : "Standard", sub: "California BMP protocols apply." }
                    ].map((item, i) => (
                        <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 15, backgroundColor: colors.slate[50], borderRadius: 12 }}>
                            <View>
                                <Text style={{ fontSize: 9, fontWeight: 'black', color: colors.slate[900] }}>{item.label}</Text>
                                <Text style={{ fontSize: 7, color: colors.slate[400], marginTop: 2, fontWeight: 'bold' }}>{item.sub}</Text>
                            </View>
                            <View style={{ paddingHorizontal: 10, paddingVertical: 5, backgroundColor: colors.white, borderRadius: 6 }}>
                                <Text style={{ fontSize: 8, fontWeight: 'black', color: colors.slate[900] }}>{item.val}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                <Footer pageNumber={7 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 7: ADU INTELLIGENCE */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>ADU Intelligence</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            California State Potential (AB 68)
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                {/* --- ADU Hero Section --- */}
                <View style={{ backgroundColor: colors.slate[900], borderRadius: 24, padding: 20, marginBottom: 15, position: 'relative', overflow: 'hidden' }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View style={{ flex: 1 }}>
                            <Text style={{ fontSize: 28, fontWeight: 'black', color: colors.white }}>Add up to <Text style={{ color: colors.indigoLight }}>2 Extra Units</Text></Text>
                            <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 8, fontWeight: 'bold' }}>
                                Under California mandate (AB 68), you are typically allowed one ADU and one JADU regardless of local density limits.
                            </Text>
                        </View>
                        <View style={{ backgroundColor: 'rgba(255,255,255,0.05)', padding: 15, borderRadius: 20, alignItems: 'center' }}>
                            <Text style={{ fontSize: 24, fontWeight: 'black', color: colors.white }}>1,700 <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>ft²</Text></Text>
                            <Text style={{ fontSize: 7, color: colors.indigoLight, fontWeight: 'black', marginTop: 2 }}>MAX COMBINED AREA</Text>
                        </View>
                    </View>
                </View>

                {/* --- ADU Cards Section --- */}
                <View style={{ flexDirection: 'row', gap: 20, marginBottom: 15 }}>
                    {/* Junior ADU */}
                    <View style={[styles.analysisCard, { flex: 1, backgroundColor: colors.white, borderSize: 1, borderColor: colors.slate[100] }]}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                            <View style={{ padding: 8, backgroundColor: colors.indigoLight, borderRadius: 10 }}>
                                <Text style={{ fontSize: 6, fontWeight: 'black', color: colors.white }}>FASTEST</Text>
                            </View>
                            <Text style={{ fontSize: 8, fontWeight: 'black', color: colors.slate[300] }}>INTERNAL CONVERSION</Text>
                        </View>
                        <Text style={{ fontSize: 14, fontWeight: 'black', color: colors.slate[900], marginBottom: 2 }}>Junior ADU</Text>
                        <Text style={{ fontSize: 12, fontWeight: 'black', color: colors.indigo, marginBottom: 20 }}>{jaduMax} <Text style={{ fontSize: 8, color: colors.slate[400] }}>sqft max</Text></Text>
                        
                        <View style={{ gap: 10 }}>
                            {["Conversion of existing rooms", "Efficiency kitchen & plumbing", "Independent external entrance"].map((item, i) => (
                                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: colors.indigoLight }} />
                                    <Text style={{ fontSize: 8, color: colors.slate[600], fontWeight: 'bold' }}>{item}</Text>
                                </View>
                            ))}
                        </View>
                    </View>

                    {/* Full ADU */}
                    <View style={[styles.analysisCard, { flex: 2, backgroundColor: colors.indigo, borderLeftWidth: 0, padding: 25 }]}>
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <View style={{ flex: 1 }}>
                                <Text style={{ fontSize: 16, fontWeight: 'black', color: colors.white, marginBottom: 2 }}>Full ADU</Text>
                                <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.6)', fontWeight: 'black', textTransform: 'uppercase', marginBottom: 20 }}>Attached or Detached</Text>

                                <View style={{ flexDirection: 'row', gap: 10, marginBottom: 15 }}>
                                    <View style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', padding: 12, borderRadius: 15 }}>
                                        <Text style={{ fontSize: 12, fontWeight: 'black', color: colors.white }}>{attachedMax} <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.4)' }}>sf</Text></Text>
                                        <Text style={{ fontSize: 6, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 'black' }}>ATTACHED LIMIT</Text>
                                    </View>
                                    <View style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', padding: 12, borderRadius: 15 }}>
                                        <Text style={{ fontSize: 12, fontWeight: 'black', color: colors.white }}>{detachedMax} <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.4)' }}>sf</Text></Text>
                                        <Text style={{ fontSize: 6, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 'black' }}>DETACHED LIMIT</Text>
                                    </View>
                                </View>
                                
                                <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.6)', fontWeight: 'bold' }}>
                                    ✓ State Law overrides local density. Full ADUs are exempt from most restrictive local FAR limits.
                                </Text>
                            </View>

                            <View style={{ width: 100, alignItems: 'center', justifyContent: 'center', marginLeft: 20 }}>
                                <View style={{ width: 70, height: 70, borderRadius: 35, borderSize: 4, borderColor: 'rgba(255,255,255,0.1)', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                    <View style={{ position: 'absolute', top: -4, left: -4, right: -4, bottom: -4, borderRadius: 40, borderSize: 4, borderColor: colors.white, borderStyle: 'solid', borderTopColor: 'transparent', borderLeftColor: 'transparent' }} />
                                    <Text style={{ fontSize: 14, fontWeight: 'black', color: colors.white }}>85%</Text>
                                    <Text style={{ fontSize: 5, color: 'rgba(255,255,255,0.5)', fontWeight: 'black' }}>POTENTIAL</Text>
                                </View>
                                <View style={{ marginTop: 10, paddingHorizontal: 8, paddingVertical: 4, backgroundColor: colors.white, borderRadius: 6 }}>
                                    <Text style={{ fontSize: 6, fontWeight: 'black', color: colors.indigo }}>HIGH POTENTIAL</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>


                <View style={[styles.noteBox, { backgroundColor: 'rgba(99, 102, 241, 0.05)', borderLeftColor: colors.indigo, borderRadius: 15 }]}>
                    <Text style={{ fontSize: 8, color: colors.indigo, lineHeight: 1.4, fontWeight: 'bold' }}>
                        Local ordinances may apply but cannot prohibit ADUs entirely. This analysis reflects standard California Housing & Community Development (HCD) protocols.
                    </Text>
                </View>

                <Footer pageNumber={8 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 8: COMPLIANCE DATA */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Regulatory Compliance</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Topographic & Municipal Audit
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <Text style={styles.sectionHeading}>Slope Distribution Compliance</Text>
                <View style={{ marginBottom: 15 }}>
                    {[
                        { label: "Standard Build (< 15%)", pass: slopeVal < 15 },
                        { label: "Hillside Review (< 25%)", pass: slopeVal < 25 },
                        { label: "Critical Slope (< 30%)", pass: slopeVal < 30 },
                        { label: "Geotechnical Report (> 15%)", pass: slopeVal <= 15, invert: true },
                    ].map((item, i) => (
                        <View key={i} style={styles.complianceCard}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: item.pass ? colors.emerald : (item.invert ? colors.amber : colors.indigo), marginRight: 15 }} />
                                <Text style={{ fontSize: 10, fontWeight: 'bold', color: colors.slate[700] }}>{item.label}</Text>
                            </View>
                            <View style={[styles.badge, { backgroundColor: item.pass ? colors.emerald : (item.invert ? colors.amber : 'rgba(0,0,0,0.05)'), color: item.pass || item.invert ? colors.white : colors.slate[400] }]}>
                                <Text>{item.pass ? "COMPLIANT" : (item.invert ? "REQUIRED" : "NOT APPLICABLE")}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                <Text style={styles.sectionHeading}>Prerequisite Documentation Checklist</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    {[
                        { doc: "Topographic Survey", req: true },
                        { doc: "Grading & Drainage Plan", req: slopeVal > 10 },
                        { doc: "Geotechnical Report", req: slopeVal > 15 },
                        { doc: "Coastal Permit (CDP)", req: requires_cdp },
                        { doc: "Design Review", req: designRev },
                        {/* doc: "CEQA Review", req: slopeVal > 25 }, */}
                    ].map((item, i) => (
                        <View key={i} style={{ width: '48%', padding: 10, backgroundColor: colors.slate[50], borderRadius: 12, borderLeftWidth: 3, borderLeftColor: item.req ? colors.amber : colors.emerald }}>
                            <Text style={{ fontSize: 9, fontWeight: 'black', color: colors.slate[800] }}>{item.doc}</Text>
                            <Text style={{ fontSize: 7, color: colors.slate[400], marginTop: 3, fontWeight: 'bold' }}>{item.req ? "ACTION REQUIRED" : "CONDITION PASSED"}</Text>
                        </View>
                    ))}
                </View>

                <View style={[styles.noteBox, { marginTop: 15, borderLeftColor: colors.indigo }]}>
                    <Text style={[styles.noteText, { fontWeight: 'bold', marginBottom: 5 }]}>COMPLIANCE & USAGE DISCLAIMER</Text>
                    <Text style={styles.noteText}>
                        This audit is based on current municipal code and LiDAR topographical data. Final determination of compliance is subject to formal city review and field verification by a licensed surveyor.
                    </Text>
                    <Text style={[styles.noteText, { marginTop: 5 }]}>
                        The Development Analyzer uses AI. We do not warrant that the services will be uninterrupted, accurate or error free. You accept and agree that any use of outputs from our service is at your sole risk and you will not rely on output as a sole source of truth or factual information, or as a substitute for professional advice.
                    </Text>
                    <Text style={[styles.noteText, { marginTop: 5 }]}>
                        The intended use of this product is to aid in the decision to invest further into a property's development potential. In no way does this tool replace the actions and outcomes of enlisting professional services that are traditionally used in real estate development.
                    </Text>
                </View>

                <Footer pageNumber={9 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 9: PROJECT ROADMAP */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Project Roadmap</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Estimated Development Schedule
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <View style={{ backgroundColor: colors.indigoDark, borderRadius: 24, padding: 20, marginBottom: 15 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 25 }}>
                        <View>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: timeline ? colors.emerald : colors.slate[400] }} />
                                <Text style={{ fontSize: 12, fontWeight: 'black', color: colors.white }}>Execution Timeline</Text>
                            </View>
                            <Text style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', fontWeight: 'bold' }}>MUNICIPAL ESTIMATION • {jurisdictionName || "LOCAL"}</Text>
                        </View>
                        <View style={{ alignItems: 'right' }}>
                            <Text style={{ fontSize: 24, fontWeight: 'black', color: colors.white }}>{timeline?.total_months || "TBD"} <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>MONTHS</Text></Text>
                        </View>
                    </View>

                    {/* Sparkline Bar */}
                    {timeline?.phases && (
                        <View style={{ height: 10, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 5, flexDirection: 'row', overflow: 'hidden', marginBottom: 30 }}>
                            {timeline.phases.map((phase, i) => {
                                const months = parseInt(phase.duration || phase.value || phase.months || 0);
                                const total = timeline.total_months || 1;
                                const width = (months / total) * 100;
                                const phaseColors = [colors.indigo, colors.amber, colors.emerald, colors.slate[400]];
                                return (
                                    <View key={i} style={{ width: `${width}%`, backgroundColor: phaseColors[i % phaseColors.length] }} />
                                );
                            })}
                        </View>
                    )}

                    {timeline?.phases ? timeline.phases.map((phase, i) => {
                        const phaseColors = [colors.indigoLight, colors.amber, colors.emerald, colors.slate[400]];
                        return (
                            <View key={i} style={{ marginBottom: 12, borderLeftWidth: 2, borderLeftColor: i === timeline.phases.length - 1 ? 'transparent' : 'rgba(255,255,255,0.1)', marginLeft: 5, paddingLeft: 20, position: 'relative' }}>
                                <View style={{ position: 'absolute', left: -6, top: 0, width: 10, height: 10, borderRadius: 5, backgroundColor: phaseColors[i % phaseColors.length], borderSize: 2, borderColor: colors.indigoDark }} />
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                    <Text style={{ fontSize: 10, fontWeight: 'black', color: colors.white }}>{phase.name || phase.label}</Text>
                                    <View style={{ backgroundColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 5 }}>
                                        <Text style={{ fontSize: 7, color: colors.white, fontWeight: 'black' }}>{phase.duration || phase.value || (phase.months ? `${phase.months} MO` : "TBD")}</Text>
                                    </View>
                                </View>
                                <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.4)', lineHeight: 1.3 }}>{phase.desc || "Estimated timeframe based on regional municipal performance averages."}</Text>
                            </View>
                        );
                    }) : (
                        <View style={{ padding: 30, alignItems: 'center' }}>
                            <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', fontWeight: 'bold' }}>Timeline modeling unavailable for this jurisdiction</Text>
                        </View>
                    )}
                </View>

                <View style={styles.noteBox}>
                    <Text style={[styles.noteText, { fontWeight: 'bold', marginBottom: 5 }]}>TIMELINE DISCLAIMER</Text>
                    <Text style={styles.noteText}>
                        These timeframes are estimates based on standard jurisdictional processing times. Factors such as design complexity, municipal staffing levels, and contractor availability may impact the actual schedule.
                    </Text>
                </View>

                <Footer pageNumber={10 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 10: DATA SOURCE INVENTORY */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>System Intelligence</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Audit Record & Source Data
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <View style={{ marginBottom: 30 }}>
                    <Text style={styles.sectionHeading}>Data Source Integrity</Text>
                    <View style={{ flexDirection: 'row', gap: 15 }}>
                        <View style={{ flex: 1, padding: 20, backgroundColor: colors.slate[50], borderRadius: 20, borderLeftWidth: 4, borderLeftColor: colors.indigoLight }}>
                            <Text style={{ fontSize: 14, fontWeight: 'black', color: colors.slate[900], marginBottom: 8 }}>Record Authenticity</Text>
                            <Text style={{ fontSize: 8, color: colors.slate[500], lineHeight: 1.5 }}>
                                This feasibility audit is a proprietary product of DevelopmentAnalyzer. All topographical data is sourced from high-resolution USGS LiDAR datasets and processed via our GIS intelligence engine.
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={{ gap: 10 }}>
                    {[
                        { label: "APN Source Data", val: apn || "County Records", icon: "RECORD" },
                        { label: "GIS Engine Version", val: gis_engine || "LIDAR-V2-ULTRA", icon: "ENGINE" },
                        { label: "Coordinate System", val: coordinate_system || "WGS84 / Web Mercator", icon: "COORD" },
                        { label: "Contour Resolution", val: demContourTrace?.interval_ft ? `${demContourTrace.interval_ft} FT` : "2 FT Standard", icon: "RESO" },
                        { label: "Jurisdiction Code", val: city_code || "LOCAL", icon: "CODE" },
                        { label: "Audit ID", val: `#${analysis.id?.toString()?.slice(-12).toUpperCase() || "AUDIT-001"}`, icon: "ID" }
                    ].map((item, i) => (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 15, backgroundColor: colors.white, borderRadius: 12, borderBottomWidth: 1, borderBottomColor: colors.slate[50] }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                <View style={{ padding: 6, backgroundColor: colors.slate[50], borderRadius: 8 }}>
                                    <Text style={{ fontSize: 6, fontWeight: 'black', color: colors.slate[300] }}>{item.icon}</Text>
                                </View>
                                <Text style={{ fontSize: 9, fontWeight: 'black', color: colors.slate[500], textTransform: 'uppercase' }}>{item.label}</Text>
                            </View>
                            <Text style={{ fontSize: 10, fontWeight: 'black', color: colors.slate[900] }}>{item.val}</Text>
                        </View>
                    ))}
                </View>

                <Footer pageNumber={11 + extraPagesCount} totalPages={totalPages} city={displayCity} />
            </Page>

            {/* PAGE 11: GLOSSARY & TERMS */}
            <Page size="A4" style={styles.contentPage}>
                <TopDisclaimer />
                <View style={styles.headerRow}>
                    <View style={{ flex: 1, marginRight: 20 }}>
                        <Text style={{ fontSize: 18, fontWeight: 'black', color: colors.slate[900], marginBottom: 5 }}>Glossary of Terms</Text>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', color: colors.slate[400], textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Technical Definitions & Methodology
                        </Text>
                    </View>
                    <BrandingLogo scale={0.75} />
                </View>

                <View style={{ gap: 15 }}>
                    {[
                        { 
                            term: analysis.mda_label && analysis.mda_label !== "MDA" 
                                ? analysis.mda_label 
                                : "MDA (Max Development Area)", 
                            def: "The maximum total horizontal footprint allowed for all structures on a lot, typically excluding certain permeable hardscapes." 
                        },
                        { 
                            term: analysis.mfa_label && analysis.mfa_label !== "MFA" 
                                ? analysis.mfa_label 
                                : "MFA (Max Floor Area)", 
                            def: "The total cumulative square footage of all floor levels allowed in the primary residence and accessory structures." 
                        },
                        { term: "LiDAR (Light Detection and Ranging)", def: "Remote sensing method used to examine the surface of the Earth. We use 2ft resolution LiDAR to calculate precise slope and contour data." },
                        { term: "ADU / JADU", def: "Accessory Dwelling Units. State law (AB 68) allows for independent living units with permanent provisions for living, sleeping, eating, and sanitation." },
                        { term: "Slope Density", def: "A regulatory formula that reduces allowable development as the natural grade of the property increases." },
                        { term: "CDP (Coastal Development Permit)", def: "A required permit for development within the designated California Coastal Zone, subject to specific environmental protections." }
                    ].map((item, i) => (
                        <View key={i} style={{ marginBottom: 10 }}>
                            <Text style={{ fontSize: 10, fontWeight: 'black', color: colors.indigo, marginBottom: 4 }}>{item.term}</Text>
                            <Text style={{ fontSize: 9, color: colors.slate[600], lineHeight: 1.4 }}>{item.def}</Text>
                        </View>
                    ))}
                </View>

                <View style={{ marginTop: 'auto', paddingBottom: 20 }}>
                    <View style={{ borderTopWidth: 1, borderTopColor: colors.slate[100], paddingTop: 20, alignItems: 'center' }}>
                        <Text style={{ fontSize: 8, color: colors.slate[400], textAlign: 'center', lineHeight: 1.5 }}>
                            Verification of all data points is recommended prior to financial commitment.{"\n"}
                            © 2026 DevelopmentAnalyzer. All rights reserved.
                        </Text>
                        <View style={{ marginTop: 15, paddingHorizontal: 15, paddingVertical: 6, backgroundColor: colors.indigo, borderRadius: 100 }}>
                            <Text style={{ fontSize: 7, fontWeight: 'black', color: colors.white }}>OFFICIAL FEASIBILITY AUDIT</Text>
                        </View>
                    </View>
                </View>

                <Footer pageNumber={12 + extraPagesCount} totalPages={totalPages} city={jurisdictionName || city_code} />
            </Page>
        </Document>
    );
};

export default AnalysisReportPDF;
